import { useCallback, useMemo, useState } from "react";
import {
  Activity,
  CalendarRange,
  Download,
  Filter,
  LogIn,
  LogOut,
  MailPlus,
  RotateCw,
  ScrollText,
  ShieldAlert,
  ShieldCheck,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from "lucide-react";
import { AUDIT_ACTIONS, type AuditLogEntry } from "@ir/types";
import {
  downloadCsv,
  formatDateTime,
  formatRelativeTime,
  listAuditLog,
  toCsv,
} from "@ir/services";
import {
  Badge,
  Button,
  CopyableId,
  DataTable,
  Field,
  Input,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Toolbar,
  type BadgeTone,
  type Column,
  type SortState,
} from "@ir/ui";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";

const AUDIT_LOG_PAGE_SIZE = 50;
type AuditSortBy = "created_at" | "actor_email" | "action" | "resource_type" | "resource_id";

function auditSortByForColumn(columnId: string): AuditSortBy {
  if (columnId === "when") return "created_at";
  if (columnId === "actor") return "actor_email";
  if (columnId === "resource") return "resource_type";
  if (["action", "resource_id"].includes(columnId)) return columnId as AuditSortBy;
  return "created_at";
}

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  ...AUDIT_ACTIONS.map((action) => ({ value: action, label: action })),
];

/** How each action reads at a glance. Unknown actions fall back to neutral. */
const ACTION_STYLE: Record<string, { tone: BadgeTone; icon: LucideIcon }> = {
  "auth.login": { tone: "success", icon: LogIn },
  "auth.logout": { tone: "neutral", icon: LogOut },
  "auth.denied": { tone: "danger", icon: ShieldAlert },
  "user.self_registered": { tone: "info", icon: UserPlus },
  "user.invited": { tone: "gold", icon: MailPlus },
  "user.invite_accepted": { tone: "success", icon: UserPlus },
  "user.invite_revoked": { tone: "warning", icon: UserMinus },
  "user.invite_resent": { tone: "gold", icon: MailPlus },
  "user.role_updated": { tone: "info", icon: ShieldCheck },
  "user.disabled": { tone: "danger", icon: UserMinus },
  "user.enabled": { tone: "success", icon: UserPlus },
};

export function AuditLogPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ columnId: "when", direction: "desc" });
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const result = useAsyncData(
    (signal) =>
      listAuditLog({
        searchTerm,
        action,
        from,
        to,
        pageNumber: page,
        pageSize: AUDIT_LOG_PAGE_SIZE,
        sortBy: auditSortByForColumn(sort.columnId),
        sortDirection: sort.direction,
        signal,
      }),
    [searchTerm, action, from, to, page, sort, refreshToken],
  );

  const exportCsv = () => {
    const rows = result.data?.items ?? [];
    if (rows.length === 0) return;
    downloadCsv(
      `ir-admin-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(
        rows.map((entry) => ({
          created_at: entry.created_at,
          actor_email: entry.actor_email,
          action: String(entry.action),
          resource_type: entry.resource_type,
          resource_id: entry.resource_id ?? "",
          ip_address: entry.ip_address ?? "",
          metadata: entry.metadata ? JSON.stringify(entry.metadata) : "",
        })),
        [
          { key: "created_at", header: "When" },
          { key: "actor_email", header: "Actor" },
          { key: "action", header: "Action" },
          { key: "resource_type", header: "Resource type" },
          { key: "resource_id", header: "Resource" },
          { key: "ip_address", header: "IP address" },
          { key: "metadata", header: "Metadata" },
        ],
      ),
    );
  };

  const columns = useMemo<Column<AuditLogEntry>[]>(
    () => [
      {
        id: "when",
        header: "When",
        width: "12rem",
        sortable: true,
        cell: (entry) => (
          <div>
            <p className="whitespace-nowrap text-content">
              {formatDateTime(entry.created_at)}
            </p>
            <p className="text-xs text-content-subtle">
              {formatRelativeTime(entry.created_at)}
            </p>
          </div>
        ),
      },
      {
        id: "action",
        header: "Action",
        width: "14rem",
        sortable: true,
        cell: (entry) => {
          const style = ACTION_STYLE[String(entry.action)] ?? {
            tone: "neutral" as BadgeTone,
            icon: Activity,
          };
          return (
            <Badge tone={style.tone} icon={style.icon}>
              {String(entry.action)}
            </Badge>
          );
        },
      },
      {
        id: "actor",
        header: "Actor",
        sortable: true,
        cell: (entry) => (
          <span className="truncate text-content-muted">{entry.actor_email}</span>
        ),
      },
      {
        id: "resource",
        header: "Resource",
        hideBelow: "lg",
        sortable: true,
        cell: (entry) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-content-subtle">
              {entry.resource_type}
            </span>
            {entry.resource_id && <CopyableId value={entry.resource_id} />}
          </span>
        ),
      },
      {
        id: "metadata",
        header: "Detail",
        hideBelow: "xl",
        cell: (entry) =>
          entry.metadata && Object.keys(entry.metadata).length > 0 ? (
            <code
              title={JSON.stringify(entry.metadata, null, 2)}
              className="block max-w-72 truncate rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-content-muted"
            >
              {JSON.stringify(entry.metadata)}
            </code>
          ) : (
            <span className="text-content-subtle">—</span>
          ),
      },
      {
        id: "ip",
        header: "IP",
        hideBelow: "xl",
        cell: (entry) => (
          <span className="tabular text-xs text-content-subtle">
            {entry.ip_address ?? "—"}
          </span>
        ),
      },
    ],
    [],
  );

  const columnVisibility = useColumnVisibility(
    "audit",
    columns,
    ["when", "action", "actor", "resource", "metadata"],
  );

  const updateSort = (next: SortState) => {
    setSort(next);
    setPage(1);
  };

  const hasFilters = Boolean(searchTerm || action || from || to);

  return (
    <>
      <PageHeader
        icon={ScrollText}
        title="Audit log"
        description="Every sign-in, denial and access change, in order."
        actions={
          <>
            <Button
              variant="secondary"
              icon={Download}
              onClick={exportCsv}
              disabled={!result.data?.items.length}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              icon={RotateCw}
              loading={result.refreshing}
              onClick={refresh}
            >
              Refresh
            </Button>
          </>
        }
      >
        {/* `sm:items-end` not `items-end`: Toolbar itself sets `sm:items-center`, and a
            base-variant class cannot override a `sm:` one — the labelled fields would
            centre against the unlabelled buttons and nothing would share a baseline. */}
        <Toolbar className="grid items-end gap-3 sm:grid-cols-[minmax(18rem,1fr)_13rem_10rem_10rem_auto_auto] sm:items-end">
          <Field label="Search" htmlFor="audit-search">
            <SearchInput
              id="audit-search"
              className="min-w-0"
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                setPage(1);
              }}
              placeholder="Search actor, resource or detail"
              ariaLabel="Search the audit log"
            />
          </Field>
          <Field label="Action" htmlFor="audit-action">
            <Select
              id="audit-action"
              icon={Filter}
              options={ACTION_OPTIONS}
              value={action}
              aria-label="Filter by action"
              onChange={(event) => {
                setAction(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field
            label="From"
            htmlFor="audit-from"
          >
            <Input
              id="audit-from"
              type="date"
              icon={CalendarRange}
              value={from}
              max={to || undefined}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          <Field
            label="To"
            htmlFor="audit-to"
          >
            <Input
              id="audit-to"
              type="date"
              icon={CalendarRange}
              value={to}
              min={from || undefined}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
          </Field>
          {hasFilters && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchTerm("");
                setAction("");
                setFrom("");
                setTo("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          )}
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
        </Toolbar>
      </PageHeader>

      <DataTable
        caption="Audit log"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(entry) => entry.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={ScrollText}
        emptyTitle={hasFilters ? "No entries match those filters" : "Nothing logged yet"}
        emptyDescription={
          hasFilters
            ? "Try widening the date range or clearing the action filter."
            : "Sign-ins and access changes will appear here as they happen."
        }
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={AUDIT_LOG_PAGE_SIZE}
          disabled={result.refreshing}
          onPageChange={setPage}
        />
      )}
    </>
  );
}
